import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";

import { getLatestOpenworkAgentContext } from "@/services/inAppAgent/openworkAgentContextBridge";

const GENERIC_WINDOW_TITLES = new Set([
  "Case Checking",
  "Mission Control",
  "Clients",
  "Home",
  "Meetings",
  "Missions",
  "Artifacts",
  "New meeting",
]);

function readWindowParam(params: Record<string, string> | undefined, keys: string[]): string | null {
  if (!params) return null;
  for (const key of keys) {
    const value = params[key]?.trim();
    if (value) return value;
  }
  return null;
}

/** Infer a display name from the focused OS window title when it names a client or onboarding mission. */
export function householdNameFromWindowTitle(appId: string, title: string | undefined): string | null {
  const trimmed = (title ?? "").trim();
  if (!trimmed || GENERIC_WINDOW_TITLES.has(trimmed)) {
    return null;
  }
  if (trimmed.endsWith(" Onboarding")) {
    const name = trimmed.slice(0, -" Onboarding".length).trim();
    return name || null;
  }
  if (appId === "case-checking" || appId === "clients" || appId === "client" || appId === "lead") {
    return trimmed;
  }
  return null;
}

export type SamplePromptHouseholdFetch = {
  caseId: string | null;
  faClientId: string | null;
  titleFallback: string | null;
  standupClient: string | null;
};

export function resolveSamplePromptHouseholdFetch(
  osContext: OsAniContextSnapshot | null | undefined,
): SamplePromptHouseholdFetch {
  const focused = osContext?.focusedWindow ?? null;
  const params = focused?.params;

  const caseId = readWindowParam(params, ["caseId", "case_id"]);
  const faClientId = readWindowParam(params, ["faClientId", "fa_client_id", "clientId", "client_id"]);

  const titleFallback = focused
    ? householdNameFromWindowTitle(focused.appId, focused.title)
    : null;

  const standupClient = getLatestOpenworkAgentContext()?.standup?.priority_client?.trim() || null;

  return {
    caseId,
    faClientId: faClientId && !caseId ? faClientId : caseId ? null : faClientId,
    titleFallback,
    standupClient,
  };
}

export function pickSamplePromptHouseholdName(params: {
  caseClientName?: string | null;
  faClientDisplayName?: string | null;
  titleFallback?: string | null;
  standupClient?: string | null;
}): string | null {
  const candidates = [
    params.caseClientName,
    params.faClientDisplayName,
    params.titleFallback,
    params.standupClient,
  ];
  for (const row of candidates) {
    const trimmed = row?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}
